import { add } from "date-fns";
import { useMemo, useState } from "react";
import { useTheme } from "../../../hooks/useTheme";
import styled from "../../../styles";
import { Button, ButtonWrap } from "../../CTA";
import { Dropdown } from "../../Dropdown";
import { Flex } from "../../Flex";
import Loader from "../../Loader";
import { Underline } from "../../Tabs";
import { Text } from "../../Text";
import { View } from "../../View";

const StyledFlex = styled(Flex)`
  display: flex;
  align-items: stretch;
  flex-direction: column;
`;

export enum BlockDurationDays {
  OneMonth = "1 Month",
  ThreeMonths = "3 Months",
  SixMonths = "6 Months",
  OneYear = "1 Year",
  Forever = "Forever",
}

interface Props {
  onBlock: (blockExpiresAt?: Date) => void;
  onUnblock: () => void;
  onCancel: () => void;
  isLoading: boolean;
  creatorName: string;
  brandName: string;
  isBlocked: boolean;
}

export const BlockCreatorOverlay = (props: Props) => {
  const [duration, setDuration] = useState<BlockDurationDays>(
    BlockDurationDays.OneMonth
  );
  const theme = useTheme();

  const today = useMemo(() => new Date(), []);

  const handleConfirm = () => {
    let expiresAt: Date | undefined = undefined;
    switch (duration) {
      case BlockDurationDays.OneMonth:
        expiresAt = add(today, { days: 30 });
        break;
      case BlockDurationDays.SixMonths:
        expiresAt = add(today, { days: 180 });
        break;
      case BlockDurationDays.OneYear:
        expiresAt = add(today, { days: 365 });
        break;
      case BlockDurationDays.Forever:
        expiresAt = undefined;
        break;
      default:
        expiresAt = undefined;
        break;
    }
    props.onBlock(expiresAt);
  };

  if (props.isBlocked) {
    return (
      <StyledFlex>
        <Text weight="bold" size="xl" margin={"0 0 0 0"} colorPreset="heading">
          Unblock Creator
        </Text>
        <Underline selected />
        <Text margin={"m 0 s 0"} colorPreset="secondary">
          {props.creatorName} will be able to view or apply to your campaigns
          again.
          <span
            style={{
              fontWeight: theme.typography.weight.semi,
              color: theme.color.typography.text,
            }}
          >
            They won’t be notified, nor will they be aware of this action.
          </span>
        </Text>

        <ButtonWrap margin="xl 0 0">
          <Button buttonType="secondary" onClick={props.onCancel}>
            Cancel
          </Button>
          <Button onClick={props.onUnblock}>
            {props.isLoading ? "Loading..." : "Unblock Creator"}
          </Button>
        </ButtonWrap>
      </StyledFlex>
    );
  }

  return (
    <>
      {props.isLoading ? (
        <Flex align="center" justify="center">
          <Loader />
        </Flex>
      ) : (
        <StyledFlex>
          <Text
            weight="bold"
            size="xl"
            margin={"0 0 0 0"}
            colorPreset="heading"
          >
            Block Creator
          </Text>
          <Underline selected />
          <Text margin={"m 0 s 0"} colorPreset="secondary">
            {props.creatorName} won’t be able to view or apply to your campaigns
            while they are blocked.{" "}
            <span
              style={{
                fontWeight: theme.typography.weight.semi,
                color: theme.color.typography.text,
              }}
            >
              They won’t be notified, nor will they be aware of this action.
            </span>
          </Text>

          <Text weight="bold" margin="s 0 xs" size="s">
            Block Period
          </Text>
          <Dropdown
            options={Object.values(BlockDurationDays).map((duration) => ({
              label: duration,
              value: duration,
            }))}
            selectedOptions={[duration]}
            setSelectedOptions={(selected) => {
              setDuration(selected[0] as BlockDurationDays);
            }}
            disableOptionSort={true}
            renderLabel={() => duration}
            selectionMode="single"
            width={200}
            size="s"
            showSelectAll={false}
            anchor="bottom end"
          />

          <ButtonWrap margin="xl 0 0">
            <Button buttonType="secondary" onClick={props.onCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>
              {props.isLoading ? "Loading..." : "Block Creator"}
            </Button>
          </ButtonWrap>
        </StyledFlex>
      )}
    </>
  );
};

export const Alert = styled(View)`
  background-color: ${(p) => p.theme.color.secondary}30;
  border-left: 3px solid ${(p) => p.theme.color.secondary};
  border-radius: 2px;
  width: 100%;
  box-sizing: border-box;

  color: ${(p) => p.theme.color.secondary};
`;
